// src/features/documents/pages/DocumentsPage.tsx
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, CheckCircle2, Clock, RotateCcw, AlertCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useDocumentsList, useSemanticSearch, useDocumentDetail, useDocumentStats, documentKeys, isDocumentAccessError } from '../hooks/useDocuments';
import { retryFailedDocuments } from '../api';
import { DocumentsTable } from '../components/DocumentsTable';
import { PreviewPanel } from '../components/PreviewPanel';
import { UploadDropzone } from '../components/UploadDropzone';
import type { Document, SearchContext } from '../types';
import { formatBytes } from '@/lib/utils';

const PANEL_MIN = 300;
const PANEL_MAX_PCT = 0.75;
const PANEL_STORAGE_KEY = 'atek_dms_panel_width';

function hasDocumentRows(value: unknown): value is { data: Document[] } {
  if (!value || typeof value !== 'object' || !('data' in value) || !Array.isArray(value.data)) return false;
  return value.data.every((doc) => doc !== null && typeof doc === 'object' && typeof doc.id === 'string');
}

export function DocumentsPage() {
  const queryClient = useQueryClient();
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedDoc, setSelectedDocState] = useState<Document | null>(null);

  // URL ↔ selection sync. The currently-open document is reflected in the
  // `?id=<uuid>` query param so the URL is shareable / bookmarkable. When a
  // user lands on /documents?id=X with no in-memory state, we fetch the doc
  // by id and select it.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlDocId = searchParams.get('id');
  // Keep observing the selected ID: a later authorization failure must also
  // invalidate the selection, not just the child panel's fetch result.
  const { data: urlDocDetail, error: detailError } = useDocumentDetail(urlDocId);
  const documentAccessDenied = isDocumentAccessError(detailError);

  const setSelectedDoc = useCallback(
    (doc: Document | null) => {
      setSelectedDocState(doc);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (doc) next.set('id', doc.id);
          else next.delete('id');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Hydrate selection from the URL (deep-link / refresh / back-forward).
  useEffect(() => {
    if (urlDocId && documentAccessDenied) {
      setSelectedDoc(null);
      const privateCache = {
        predicate: (query: { queryKey: readonly unknown[] }) => query.queryKey[0] === 'documents'
          && query.queryKey[2] === urlDocId
          && ['detail', 'preview-url', 'download-url', 'links', 'references'].includes(String(query.queryKey[1])),
      };
      // Cancel signed-URL requests too, so an in-flight response cannot restore
      // a capability after the detail request has denied access.
      void queryClient.cancelQueries(privateCache, { revert: false }).then(() => {
        queryClient.removeQueries(privateCache);
      });
      const collections = queryClient.getQueryCache().findAll({
        predicate: (query) => query.queryKey[0] === 'documents'
          && ['list', 'search', 'semantic'].includes(String(query.queryKey[1])),
      });
      for (const query of collections) {
        const cached: unknown = query.state.data;
        if (cached === undefined) continue;
        if (hasDocumentRows(cached)) {
          queryClient.setQueryData(query.queryKey, { ...cached, data: cached.data.filter((doc) => doc.id !== urlDocId) });
        } else {
          // Old sessions/HMR can retain other response envelopes. Clear their
          // unknown payload entirely and refetch active readers from the API.
          void queryClient.resetQueries({ queryKey: query.queryKey, exact: true });
        }
      }
    } else if (urlDocId && urlDocDetail && (!selectedDoc || selectedDoc.id !== urlDocId)) {
      setSelectedDocState(urlDocDetail as unknown as Document);
    } else if (!urlDocId && selectedDoc) {
      setSelectedDocState(null);
    }
  }, [urlDocId, urlDocDetail, selectedDoc, documentAccessDenied, queryClient, setSelectedDoc]);
  const [previewTab, setPreviewTab] = useState(() => localStorage.getItem('atek_dms_preview_tab') || 'preview');
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem(PANEL_STORAGE_KEY);
    return saved ? parseInt(saved, 10) : 0;
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const handleTabChange = useCallback((tab: string) => {
    setPreviewTab(tab);
    localStorage.setItem('atek_dms_preview_tab', tab);
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth || (containerRef.current ? containerRef.current.offsetWidth * 0.45 : 500);

    const onMove = (ev: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const delta = startX - ev.clientX;
      const containerWidth = containerRef.current.offsetWidth;
      const newWidth = Math.min(Math.max(startWidth + delta, PANEL_MIN), containerWidth * PANEL_MAX_PCT);
      setPanelWidth(newWidth);
    };

    const onUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setPanelWidth((w) => { localStorage.setItem(PANEL_STORAGE_KEY, String(w)); return w; });
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [panelWidth]);

  const cursor = cursors[pageIndex] ?? null;
  const { data, isLoading } = useDocumentsList(cursor, searchQuery);
  const { data: semanticData, isLoading: semanticLoading } = useSemanticSearch(searchQuery);

  const documents = useMemo(() => (hasDocumentRows(data) ? data.data : []).filter(
    (doc) => !documentAccessDenied || doc.id !== urlDocId,
  ), [data, documentAccessDenied, urlDocId]);
  const hasMore = data?.meta?.hasMore ?? false;

  // Deduplicate: remove semantic results that already appear in keyword results
  const keywordDocIds = useMemo(() => new Set(documents.map((d) => d.id)), [documents]);
  const semanticResults = useMemo(() => {
    if (!hasDocumentRows(semanticData)) return [];
    return semanticData.data.filter((d) => !keywordDocIds.has(d.id)
      && (!documentAccessDenied || d.id !== urlDocId));
  }, [semanticData, keywordDocIds, documentAccessDenied, urlDocId]);

  // Track cursors for forward pagination
  if (data?.meta?.cursor && pageIndex === cursors.length - 1) {
    setCursors((prev) => {
      if (prev[prev.length - 1] === data.meta.cursor) return prev;
      return [...prev, data.meta.cursor];
    });
  }

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setPageIndex(0);
    setCursors([null]);
    setSearchQuery(searchInput.trim());
  }, [searchInput]);

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setSearchQuery('');
    setPageIndex(0);
    setCursors([null]);
  }, []);

  // Build search context for the selected document
  const searchContext: SearchContext | null = useMemo(() => {
    if (!searchQuery || !selectedDoc) return null;
    return {
      query: searchQuery,
      matchType: selectedDoc.matchType ?? null,
      matchedChunk: selectedDoc.matchedChunk ?? null,
      searchHeadline: selectedDoc.searchHeadline ?? null,
    };
  }, [searchQuery, selectedDoc]);

  const [retrying, setRetrying] = useState(false);
  const handleRetryFailed = useCallback(async () => {
    setRetrying(true);
    try {
      const result = await retryFailedDocuments();
      await queryClient.invalidateQueries({ queryKey: documentKeys.stats() });
      alert(`Queued ${result.reset} document(s) for retry.`);
    } catch {
      alert('Failed to retry documents.');
    } finally {
      setRetrying(false);
    }
  }, [queryClient]);

  const statsQuery = useDocumentStats();
  const stats = statsQuery.data;

  return (
    <div className="flex flex-col h-full">
      {/* Stats */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        <div className="bg-background rounded-lg border p-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">
            <FileText className="h-3.5 w-3.5" /> Total
          </div>
          <div className="text-2xl font-bold mt-1">{stats?.totalDocuments ?? '—'}</div>
        </div>
        <div className="bg-background rounded-lg border p-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Ready
          </div>
          <div className="text-2xl font-bold mt-1 text-green-600">{stats?.ready ?? '—'}</div>
        </div>
        <div className="bg-background rounded-lg border p-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">
            <Clock className="h-3.5 w-3.5 text-yellow-600" /> Pending
          </div>
          <div className="text-2xl font-bold mt-1 text-yellow-600">{stats?.pending ?? '—'}</div>
        </div>
        <div className="bg-background rounded-lg border p-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">
            <FileText className="h-3.5 w-3.5" /> Size
          </div>
          <div className="text-2xl font-bold mt-1">{stats ? formatBytes(stats.totalSize) : '—'}</div>
        </div>
        <div className="bg-background rounded-lg border p-3 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">
              <AlertCircle className="h-3.5 w-3.5 text-red-500" /> Failed
            </div>
            <div className="text-2xl font-bold mt-1 text-red-500">{stats?.failed ?? '—'}</div>
          </div>
          {(stats?.failed ?? 0) > 0 && (
            <button
              onClick={handleRetryFailed}
              disabled={retrying}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              <RotateCcw className={`h-3.5 w-3.5 ${retrying ? 'animate-spin' : ''}`} />
              {retrying ? 'Retrying…' : 'Retry all'}
            </button>
          )}
        </div>
      </div>

      <UploadDropzone />

      <div ref={containerRef} className="flex flex-1 min-h-0 mt-4">
        {/* Table */}
        <div className="flex-1 min-w-0 overflow-auto">
          <DocumentsTable
            documents={documents}
            semanticResults={semanticResults}
            semanticLoading={semanticLoading}
            isLoading={isLoading}
            pageIndex={pageIndex}
            hasMore={hasMore}
            searchQuery={searchQuery}
            searchInput={searchInput}
            selectedDoc={selectedDoc}
            onSelectDoc={setSelectedDoc}
            onSearchInputChange={setSearchInput}
            onSearch={handleSearch}
            onClearSearch={handleClearSearch}
            onPrevPage={() => setPageIndex((p) => p - 1)}
            onNextPage={() => setPageIndex((p) => p + 1)}
          />
        </div>

        {/* Resize handle + preview panel */}
        {selectedDoc && !documentAccessDenied && selectedDoc.id === urlDocId && (
          <>
            <div
              className="w-1 shrink-0 cursor-col-resize hover:bg-blue-400 active:bg-blue-500 transition-colors bg-border"
              onMouseDown={handleDragStart}
            />
            <div className="shrink-0 min-h-0" style={{ width: panelWidth || '45%' }}>
              <PreviewPanel
                key={selectedDoc.id}
                doc={selectedDoc}
                activeTab={previewTab}
                onTabChange={handleTabChange}
                onClose={() => setSelectedDoc(null)}
                searchContext={searchContext}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
